/* eslint-disable no-underscore-dangle */
import React, { useRef, useState, useEffect, useContext, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce, cloneDeep } from 'lodash';
import 'flatpickr/dist/themes/airbnb.css';
import { Icon } from 'semantic-ui-react';
import Flatpickr from 'react-flatpickr';
import PropTypes from 'prop-types';

import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import { DateTimeWrapper, DateWrapper, updateCalendarUI } from './date-time.styled';
import { getDatesInRange, getEmptyTableFields } from '../../../../shared/utils';
import { ctrlDefaults, DateTimeType } from '../../../../shared/form-lib';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { controlValidate, getStateValue } from '../../../form/utils';
import { FormContext } from '../../../shared/contexts/form-context';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { selectCtrlIdLabelMap } from '../../selectors';
import { ControlPropType } from '../../ctrl_proptype';
import {
  ValidationWarning,
  MainfieldWrapper,
  SubtextWrapper,
  FieldWrapper,
  LabelWrapper,
  InputWrapper,
  Field,
} from '../../common-field-styles';

const DateTime = ({ ctrl, inTable, col, tr, formDesign }) => {
  const { inCustomise, inBuilder, inForm, currentPageNo } = useContext(FormContext);
  const { userCustomTexts, form } = useSelector((state) => state.form);
  const ctrlProps = inTable ? ctrl.table_props?.[col - 1] : ctrl.props;
  const responseStore = form?.response_store ?? 'formplus';
  const dispatch = useDispatch();

  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];

  const [newCtrl, setNewCtrl] = useState(ctrl);
  const [ctrlValidationError, setCtrlValidationError] = useState(
    getStateValue(ctrl._validationError, col, tr, inTable, inBuilder)
  );
  
  const dateType = ctrlProps?.type;
  const dateMode = ctrlProps.mode || 'single';
  const timeFormat = ctrlProps.time_format || '24';
  const dateSeparator = ctrlProps.separator || '/';
  const disabledDays = ctrlProps.disabled_days || [];
  const disableYear = ctrlProps.disable_year || false;
  const disabledDates = ctrlProps.disabled_dates || [];
  const dateFormat = ctrlProps.date_format || 'YYYY/MM/DD';
  const dateValue = getStateValue(ctrl.value, col, tr, inTable);
  const disabledDatesInRange = ctrlProps.disabled_date_range || '';
  const disableYearEffect = dateType === DateTimeType.DATE && disableYear;

  const datePlaceholder =
    dateType === DateTimeType.TIME
      ? 'Select Time'
      : dateType === DateTimeType.DATE
      ? 'Select Date'
      : 'Select Date and Time';
  const newDisabledDatesInRange = inForm && getDatesInRange(disabledDatesInRange, dateFormat, dateSeparator);
  const newDisabledDates = [...new Set([...disabledDates, ...(newDisabledDatesInRange || [])])];
  const hasDisabledDates =
    !disableYearEffect &&
    dateMode !== 'range' &&
    (dateType === DateTimeType.DATETIME || dateType === DateTimeType.DATE)
      ? disabledDays.length || newDisabledDates.length
      : false;

  useEffect(() => {
    setCtrlValidationError(getStateValue(ctrl._validationError, col, tr, inTable, inBuilder));
  }, [ctrl._validationError, inTable, col, tr]);

  const saveUpdatedCtrl = useRef(
    debounce((newValue) => {
      dispatch(
        setUserInputOnCtrl({
          newValue,
          property: 'value',
          page: currentPageNo - 1,
          ctrlId: ctrl.internal_id,
          isValid: true,
          ...(inTable && { tr: tr - 1, col: col - 1 }),
        })
      );
    }, 100)
  );

  function handleDateTimeChange(dateStr) {
    if (inTable) {
      const control = cloneDeep(newCtrl);
      if (!newCtrl.value) {
        control.value = getEmptyTableFields(newCtrl.props.min_rows, newCtrl.props.columns);
        control._validationError = getEmptyTableFields(newCtrl.props.min_rows, newCtrl.props.columns);
        control.value[tr - 1][col - 1] = dateStr;
        setNewCtrl(control);
      } else {
        control.value[tr - 1][col - 1] = dateStr;
        setNewCtrl(control);
      }
      controlValidate({
        isPayOnSubmission: false,
        setCtrlValidationError,
        userCustomTexts,
        ctrl: control,
        responseStore,
        setNewCtrl,
        col,
        tr
      });
    } else {
      setNewCtrl((prevCtrl) => ({ ...cloneDeep(prevCtrl), value: dateStr }));
      controlValidate({
        ctrl: { ...cloneDeep(ctrl), value: dateStr },
        isPayOnSubmission: false,
        setCtrlValidationError,
        userCustomTexts,
        responseStore,
        setNewCtrl,
        col,
        tr
      });
    }
    // store in global state
    saveUpdatedCtrl.current(dateStr);
  }

  function createFlatpickrOptions() {
    const dateNow = new Date();
    const timeNow = `${dateNow.getHours().toString().padStart(2, '0')}:${dateNow
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
    let newDateFormat, minDate, maxDate, minTime, maxTime;

    const dateFormatOptions = {
      'MM/DD/YYYY': `m${dateSeparator}d${dateSeparator}Y`,
      'DD/MM/YYYY': `d${dateSeparator}m${dateSeparator}Y`,
      'YYYY/MM/DD': `Y${dateSeparator}m${dateSeparator}d`,
    };

    const dateFormatOption = disableYearEffect ? 'J F, Y' : dateFormatOptions[dateFormat];

    // NOTE: When disableYearEffect, calender is scoped to 2024 (a non-leap year).

    if (dateType === DateTimeType.DATE || dateType === DateTimeType.DATETIME) {
      newDateFormat =
        dateType === DateTimeType.DATE
          ? dateFormatOption
          : `${dateFormatOption} ${timeFormat === '24' ? 'H:i' : 'h:i K'}`;
      minDate = disableYearEffect
        ? '2024-01-01'
        : ctrl.props.past
        ? dateNow
        : ctrl.props.min_date
        ? new Date(ctrl.props.min_date)
        : '';
      maxDate = disableYearEffect
        ? '2024-12-31'
        : ctrl.props.future
        ? dateNow
        : ctrl.props.max_date
        ? new Date(ctrl.props.max_date)
        : '';
    } else if (dateType === DateTimeType.TIME) {
      newDateFormat = timeFormat === '24' ? 'H:i' : 'h:i K';
      minTime = ctrl.props.past ? timeNow : ctrl.props.min_time;
      maxTime = ctrl.props.future ? timeNow : ctrl.props.max_time;
    }
    const time_24hr = timeFormat === '24' ? true : false;
    const forceDisableMobile = minDate || maxDate || minTime || maxTime || disableYearEffect;
    const newValue = disableYearEffect ? `${dateValue} 2024` : dateValue;

    const commonOptions = {
      defaultDate: newValue,
      onChange: (_, dateStr, instance) => {
        const newDateStr = disableYearEffect ? instance.altInput.value : dateStr;
        handleDateTimeChange(newDateStr);
      },
      onClose: (_, dateStr, instance) => {
        const newDateStr = disableYearEffect ? instance.altInput.value : dateStr;
        if (instance.isMobile) return; // Important
        handleDateTimeChange(newDateStr);
      },
      onReady: (_, __, instance) => {
        updateCalendarUI(instance, formDesign, disableYearEffect);
      },
      ...(disableYearEffect && {
        altFormat: 'J F', // Show date without the year e.g: 16th January, 2024 ('J F, Y') ===> 16th January ('J F')
        altInput: true,
      }),
    };
    
    return {
      minDate,
      maxDate,
      minTime,
      maxTime,
      time_24hr,
      dateFormat: newDateFormat,
      ariaDateFormat: newDateFormat,
      mode: disableYearEffect ? 'single' : dateMode,
      disableMobile: forceDisableMobile ? true : false,
      ...(hasDisabledDates && {
        disable: [(date) => disabledDays.includes(date.getDay().toString()), ...newDisabledDates],
      }),
      ...commonOptions,
    };
    // More info about flatpickr config options at https://flatpickr.js.org/options/
  }

  useLayoutEffect(() => {
    if (inForm) {
      // initialize Flatpickr
      const elementId = inTable ? `fpl-${ctrl.internal_id}-${col}-${tr}` : `fpl-${ctrl.internal_id}`;
      let fpInstance = document.getElementById(elementId)?._flatpickr;

      if (fpInstance) fpInstance.destroy();
      fpInstance = flatpickr(`#${elementId}`, createFlatpickrOptions());

      return () => {
        if (fpInstance) fpInstance.destroy();
      };
    }
  }, []);

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {ctrlValidationError && (
          <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
        )}

        <Field className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
          {!inTable && (
            <LabelWrapper
              required={ctrl.props.required}
              notInBuilder={!inBuilder}
              className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
              <label
                htmlFor={`fpl-${ctrl.internal_id}`}
                dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }}
              />
              <SpecialPropsIndicators ctrl={ctrl} />
            </LabelWrapper>
          )}

          <InputWrapper className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
            <DateTimeWrapper>
              <DateWrapper notInBuilder={!inBuilder}>
                <div className="flatpickr">
                  <Flatpickr
                    data-testid="date_time"
                    placeholder={datePlaceholder}
                    className="drop-down date-picker"
                    data-enable-time={dateType !== DateTimeType.DATE}
                    data-no-calendar={dateType === DateTimeType.TIME}
                    disabled={ctrl.props.readonly || inCustomise || inBuilder}
                    title={`${ctrl.props.readonly ? 'This field cannot be edited' : ''}`}
                    id={!inTable ? `fpl-${ctrl.internal_id}` : `fpl-${ctrl.internal_id}-${col}-${tr}`}
                  />

                  <Icon name={dateType === DateTimeType.TIME ? 'clock' : 'calendar'} color="grey" />
                </div>
              </DateWrapper>
            </DateTimeWrapper>

            {!inTable && (
              <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
                {ctrl.props.subtext}
              </SubtextWrapper>
            )}
          </InputWrapper>
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

DateTime.defaultProps = {
  inTable: false,
  col: null,
  tr: null,
  formDesign: null,
};

DateTime.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  inTable: PropTypes.bool,
  col: PropTypes.number,
  tr: PropTypes.number,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};

export default DateTime;
